import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, User, Users, TrendingUp } from 'lucide-react';

const getRankColor = rank => ({ 'Bronze': 'bg-amber-700 text-white', 'Silver': 'bg-slate-400 text-white', 'Gold': 'bg-yellow-500 text-white', 'Platinum': 'bg-slate-600 text-white', 'Diamond': 'bg-cyan-500 text-white' }[rank] || 'bg-slate-500 text-white');
const getStatusColor = status => ({ 'active': 'bg-green-100 text-green-800', 'pending': 'bg-yellow-100 text-yellow-800', 'suspended': 'bg-red-100 text-red-800' }[status] || 'bg-slate-100 text-slate-800');

// Single node render without recursion
function renderNode(node, level, expandedNodes, onToggle) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes[node.id] ?? (level < 2);
  
  const items = [];
  
  // Render current node
  items.push(
    <div key={node.id} className="select-none">
      <div 
        className={`flex items-center gap-2 p-3 rounded-lg mb-1 cursor-pointer transition-all hover:bg-slate-100 ${level === 0 ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500' : 'bg-white border border-slate-100'}`}
        style={{ marginLeft: level * 24 }}
        onClick={() => hasChildren && onToggle(node.id)}
      >
        {hasChildren ? (
          <span className="text-slate-400">{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
        ) : (
          <span className="w-4" />
        )}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${level === 0 ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
          {node.name?.charAt(0) || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 truncate">{node.name}</span>
            <Badge className={getRankColor(node.rank)}>{node.rank}</Badge>
            <Badge className={getStatusColor(node.status)}>{node.status}</Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span className="flex items-center gap-1"><User size={10} />{node.referral_code}</span>
            <span className="flex items-center gap-1"><Users size={10} />{node.direct_team_count || 0} direct</span>
            <span className="flex items-center gap-1"><TrendingUp size={10} />₹{((node.total_sales || 0) / 100000).toFixed(1)}L sales</span>
          </div>
        </div>
        {hasChildren && <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full">{node.children.length} downline</span>}
      </div>
    </div>
  );
  
  // Render children if expanded
  if (hasChildren && isExpanded) {
    node.children.forEach(child => {
      const childItems = renderNode(child, level + 1, expandedNodes, onToggle);
      items.push(
        <div key={`wrapper-${child.id}`} className="border-l-2 border-dashed border-slate-200 ml-4">
          {childItems}
        </div>
      );
    });
  }
  
  return items;
}

export default function MLMTreeNode({ node, level = 0, expandedNodes, onToggle }) {
  return <>{renderNode(node, level, expandedNodes, onToggle)}</>;
}
